import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import theme from "../../theme";
import { mobile } from "../../responsive";

/* ─── Keyframes ─────────────────────────────────────────── */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const inkFade = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const scrollBounce = keyframes`
  0%, 100% { transform: translateY(0); opacity: 1; }
  50%       { transform: translateY(6px); opacity: 0.5; }
`;

/* ─── Navbar ─────────────────────────────────────────────── */

const Nav = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 56px;
  background: ${theme.bg};
  border-bottom: 1px solid ${theme.separator};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
`;

const NavBrand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 9px;
  text-decoration: none;
`;

const NavMark = styled.div`
  width: 28px;
  height: 28px;
  background: ${theme.textMain};
  color: ${theme.cardBg};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Georgia', serif;
  font-style: italic;
  font-size: 15px;
  font-weight: 400;
`;

const NavName = styled.span`
  font-family: 'Georgia', serif;
  font-size: 15px;
  font-weight: 400;
  color: ${theme.textMain};
  letter-spacing: -0.2px;
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const NavLink = styled(Link)`
  font-size: 13.5px;
  font-weight: 600;
  color: ${theme.textSub};
  text-decoration: none;
  padding: 7px 14px;
  border-radius: 7px;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: ${theme.textMain};
    background: ${theme.bgAlt};
  }
`;

const NavCta = styled(Link)`
  font-size: 13.5px;
  font-weight: 600;
  color: ${theme.cardBg};
  text-decoration: none;
  padding: 7px 16px;
  border-radius: 7px;
  background: ${theme.textMain};
  transition: background 0.15s, transform 0.15s;

  &:hover {
    background: ${theme.primary};
    transform: translateY(-1px);
  }
`;

/* ─── Layout ─────────────────────────────────────────────── */

const Page = styled.div`
  background: ${theme.bg};
  overflow-x: hidden;
  padding-top: 56px;
`;

/* ─── Hero / Book Section ────────────────────────────────── */

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 40px 24px 80px;
`;

const HeroTagline = styled.p`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${theme.primary};
  margin-bottom: 20px;
  animation: ${fadeUp} 0.6s ease both;
`;

const HeroTitle = styled.h1`
  font-family: 'Georgia', serif;
  font-size: clamp(36px, 6vw, 72px);
  font-weight: 400;
  color: ${theme.textMain};
  text-align: center;
  line-height: 1.15;
  letter-spacing: -1.5px;
  margin-bottom: 16px;
  animation: ${fadeUp} 0.6s 0.1s ease both;

  em {
    font-style: italic;
    color: ${theme.primary};
  }
`;

/* Highlight wrap around "open book" */
const Highlight = styled.span`
  position: relative;
  display: inline-block;
  font-style: italic;
  color: ${theme.primary};

  svg.underline-curve {
    position: absolute;
    bottom: -10px;
    left: -4px;
    width: calc(100% + 8px);
    height: 14px;
    overflow: visible;
    pointer-events: none;
  }
`;

const HeroSub = styled.p`
  font-size: 16px;
  color: ${theme.textMuted};
  text-align: center;
  max-width: 480px;
  line-height: 1.65;
  margin-bottom: 48px;
  animation: ${fadeUp} 0.6s 0.2s ease both;
`;

/* ─── Book ───────────────────────────────────────────────── */

const BookScene = styled.div`
  perspective: 1200px;
  margin-bottom: 48px;
  will-change: transform;
`;

const BookWrap = styled.div`
  position: relative;
  width: 220px;
  height: 280px;
`;

/* Book body — the back/pages stack, always visible */
const BookBody = styled.div`
  position: absolute;
  left: 22px;
  top: 0;
  right: 0;
  bottom: 0;
  border-radius: 0 14px 14px 0;
  background: #fdfaf6;
  box-shadow: -6px 6px 20px rgba(0,0,0,0.28), inset -3px 0 8px rgba(0,0,0,0.08);
  overflow: hidden;
`;

/* Red margin line */
const MarginLine = styled.div`
  position: absolute;
  left: 26px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(192, 57, 43, 0.35);
  z-index: 1;
`;

/* Ruled lines background */
const RuledLines = styled.div`
  position: absolute;
  inset: 0;
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const RuledLine = styled.div`
  height: 18px;
  border-bottom: 1px solid rgba(180, 170, 155, 0.5);
  flex-shrink: 0;
`;

/* Note content layer on top of ruled lines */
const NoteContent = styled.div`
  position: absolute;
  inset: 0;
  padding: 10px 12px 10px 32px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 0;
  animation: ${inkFade} 0.4s ease both;
`;

const NoteHeading = styled.div`
  font-family: 'Georgia', serif;
  font-size: 7.5px;
  font-weight: 700;
  color: ${theme.primary};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  line-height: 18px;
  margin-bottom: 0;
`;

const NoteSubHeading = styled.div`
  font-family: 'Georgia', serif;
  font-size: 6.5px;
  font-weight: 700;
  color: ${theme.textSub};
  letter-spacing: 0.04em;
  line-height: 18px;
  margin-top: 2px;
`;

const NoteText = styled.div`
  font-size: 6px;
  color: rgba(28, 23, 16, ${({ $opacity }) => $opacity || 0.75});
  line-height: 18px;
  width: ${({ $w }) => $w || "100%"};
  white-space: nowrap;
  overflow: hidden;
  font-family: 'Georgia', serif;
  font-style: ${({ $italic }) => $italic ? "italic" : "normal"};
`;

const NoteBlank = styled.div`
  height: 18px;
`;

/* Spine strip */
const BookSpine = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 22px;
  background: ${theme.primary};
  border-radius: 4px 0 0 4px;
  box-shadow: inset -2px 0 6px rgba(0,0,0,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4;
`;

const SpineText = styled.span`
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  font-family: 'Georgia', serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: rgba(255,255,255,0.7);
  text-transform: uppercase;
`;

/* Front cover — this is the page that "opens" */
const BookCover = styled.div`
  position: absolute;
  left: 22px;
  top: 0;
  right: 0;
  bottom: 0;
  border-radius: 0 14px 14px 0;
  transform-origin: left center;
  transform-style: preserve-3d;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 0.3s ease;
  transform: ${({ $open }) => $open ? "perspective(1200px) rotateY(-180deg)" : "perspective(1200px) rotateY(0deg)"};
  box-shadow: ${({ $open }) =>
    $open ? "none" : "4px 4px 16px rgba(0,0,0,0.18), inset -1px 0 4px rgba(0,0,0,0.06)"};
  z-index: 3;
`;

const CoverFront = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 0 14px 14px 0;
  background: ${theme.cardBg};
  border: 1.5px solid ${theme.cardBorder};
  border-left: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
`;

const CoverBack = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 0 14px 14px 0;
  background: ${theme.bgAlt};
  transform: rotateY(180deg);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
`;

const CoverLogo = styled.div`
  width: 48px;
  height: 48px;
  background: ${theme.textMain};
  color: ${theme.cardBg};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Georgia', serif;
  font-style: italic;
  font-size: 26px;
  font-weight: 400;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
`;

const CoverTitle = styled.p`
  font-family: 'Georgia', serif;
  font-size: 18px;
  font-weight: 400;
  color: ${theme.textMain};
  text-align: center;
  letter-spacing: -0.3px;
`;

const CoverEdition = styled.p`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${theme.textMuted};
`;

/* ─── Scroll hint ────────────────────────────────────────── */

const ScrollHint = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  opacity: ${({ $hide }) => ($hide ? 0 : 1)};
  transition: opacity 0.4s ease;
  animation: ${fadeUp} 0.6s 0.5s ease both;
`;

const ScrollText = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${theme.textMuted};
`;

const ScrollArrow = styled.div`
  width: 20px;
  height: 20px;
  border-right: 2px solid ${theme.textMuted};
  border-bottom: 2px solid ${theme.textMuted};
  transform: rotate(45deg);
  animation: ${scrollBounce} 1.4s ease-in-out infinite;
`;

/* ─── Content sections (revealed after book opens) ──────── */

const ContentWrapper = styled.div`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) => ($visible ? "translateY(0)" : "translateY(24px)")};
  transition: opacity 0.4s ease, transform 0.4s ease;
`;

/* ─── Features ───────────────────────────────────────────── */

const FeaturesSection = styled.section`
  padding: 80px 24px;
  max-width: 1000px;
  margin: 0 auto;
`;

const SectionLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${theme.primary};
  margin-bottom: 12px;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-family: 'Georgia', serif;
  font-size: clamp(26px, 4vw, 42px);
  font-weight: 400;
  color: ${theme.textMain};
  text-align: center;
  letter-spacing: -0.8px;
  margin-bottom: 56px;
  line-height: 1.2;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  ${mobile(`grid-template-columns: 1fr;`)}

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: ${theme.cardBg};
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 14px;
  padding: 28px 24px;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover {
    border-color: ${theme.primary};
    box-shadow: 0 8px 32px ${theme.primaryGlow};
    transform: translateY(-3px);
  }
`;

const FeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${theme.primaryLight};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: ${theme.primary};
`;

const FeatureName = styled.h3`
  font-family: 'Georgia', serif;
  font-size: 17px;
  font-weight: 400;
  color: ${theme.textMain};
  margin-bottom: 8px;
  letter-spacing: -0.2px;
`;

const FeatureDesc = styled.p`
  font-size: 13.5px;
  color: ${theme.textMuted};
  line-height: 1.6;
`;

/* ─── Stats strip ────────────────────────────────────────── */

const StatsSection = styled.section`
  background: ${theme.textMain};
  padding: 56px 24px;
`;

const StatsInner = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  text-align: center;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const StatNum = styled.div`
  font-family: 'Georgia', serif;
  font-size: 42px;
  font-weight: 400;
  color: ${theme.cardBg};
  letter-spacing: -1px;
  line-height: 1;
  margin-bottom: 6px;

  span {
    color: ${theme.primary};
  }
`;

const StatLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.45);
`;

/* ─── CTA ────────────────────────────────────────────────── */

const CtaSection = styled.section`
  padding: 100px 24px;
  text-align: center;
`;

const CtaTitle = styled.h2`
  font-family: 'Georgia', serif;
  font-size: clamp(28px, 4vw, 48px);
  font-weight: 400;
  color: ${theme.textMain};
  letter-spacing: -1px;
  margin-bottom: 16px;
  line-height: 1.2;
`;

const CtaSub = styled.p`
  font-size: 15px;
  color: ${theme.textMuted};
  margin-bottom: 36px;
  line-height: 1.6;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${theme.textMain};
  color: ${theme.cardBg};
  text-decoration: none;
  border-radius: 9px;
  padding: 13px 28px;
  font-size: 14.5px;
  font-weight: 600;
  font-family: inherit;
  transition: background 0.2s, transform 0.15s;

  &:hover {
    background: ${theme.primary};
    transform: translateY(-2px);
  }
`;

const SecondaryBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  color: ${theme.textMain};
  text-decoration: none;
  border-radius: 9px;
  padding: 13px 28px;
  font-size: 14.5px;
  font-weight: 600;
  font-family: inherit;
  border: 1.5px solid ${theme.cardBorderStrong};
  transition: border-color 0.2s, transform 0.15s;

  &:hover {
    border-color: ${theme.primary};
    color: ${theme.primary};
    transform: translateY(-2px);
  }
`;

/* ─── SVG Icons ──────────────────────────────────────────── */

const IconShare = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

const IconFolder = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
  </svg>
);

const IconUsers = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const IconChat = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const IconBarChart = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
  </svg>
);

const IconSearch2 = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

/* ─── Data ───────────────────────────────────────────────── */

const FEATURES = [
  {
    icon: <IconShare />,
    name: "Share your notes",
    desc: "Upload and share your study notes with classmates and the wider community.",
  },
  {
    icon: <IconFolder />,
    name: "Organise with folders",
    desc: "Keep everything tidy. Group notes into folders by subject, semester, or topic.",
  },
  {
    icon: <IconUsers />,
    name: "Community feed",
    desc: "Discover notes from others, leave comments, and learn together.",
  },
  {
    icon: <IconChat />,
    name: "Built-in chat",
    desc: "Message classmates and educators directly — discuss notes without leaving the app.",
  },
  {
    icon: <IconBarChart />,
    name: "Analytics",
    desc: "See how your notes are performing — views, saves, and engagement at a glance.",
  },
  {
    icon: <IconSearch2 />,
    name: "Find anyone",
    desc: "Search for students and educators by name or institution.",
  },
];

/* ─── Component ──────────────────────────────────────────── */

export default function Landing() {
  const [bookOpen, setBookOpen] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const openRef = useRef(false);
  const rafRef = useRef(null);

  useEffect(() => {
    // Guard: ignore events fired in the first 400ms (browser init scroll/wheel noise)
    let ready = false;
    const readyTimer = setTimeout(() => { ready = true; }, 400);

    const open = () => {
      if (openRef.current) return;
      openRef.current = true;
      setBookOpen(true);
    };

    const close = () => {
      if (!openRef.current) return;
      openRef.current = false;
      setBookOpen(false);
      setContentVisible(false);
    };

    const handleWheel = (e) => {
      if (!ready) return;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (e.deltaY > 0) open();
        else if (window.scrollY < 10) close();
      });
    };

    const handleScroll = () => {
      if (!ready) return;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (window.scrollY > window.innerHeight * 0.12) open();
        else if (window.scrollY < 20) close();
      });
    };

    window.addEventListener("wheel", handleWheel, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      clearTimeout(readyTimer);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Reveal content right after book opens — no artificial delay
  useEffect(() => {
    if (bookOpen) {
      const t = setTimeout(() => setContentVisible(true), 280);
      return () => clearTimeout(t);
    }
  }, [bookOpen]);

  return (
    <Page>
      {/* ── Navbar ── */}
      <Nav>
        <NavBrand to="/">
          <NavMark>N</NavMark>
          <NavName>NotesHub</NavName>
        </NavBrand>
        <NavActions>
          <NavLink to="/login">Sign in</NavLink>
          <NavCta to="/register">Get started</NavCta>
        </NavActions>
      </Nav>

      {/* ── Hero ── */}
      <HeroSection>
        <HeroTagline>Your academic knowledge hub</HeroTagline>

        <HeroTitle>
          Every great idea starts<br />
          with an{" "}
          <Highlight>
            open book
            <svg className="underline-curve" viewBox="0 0 120 14" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
              <path
                d="M2 9 C20 3, 50 14, 80 7 C100 2, 112 10, 118 8"
                stroke={theme.primary}
                strokeWidth="2.5"
                strokeLinecap="round"
                fill="none"
                opacity="0.7"
              />
            </svg>
          </Highlight>
        </HeroTitle>

        <HeroSub>
          NotesHub is where students and educators share, discover,
          and organise study notes — all in one place.
        </HeroSub>

        {/* Book */}
        <BookScene>
          <BookWrap>
            {/* Book body: ruled page always visible behind cover */}
            <BookBody>
              <MarginLine />
              {/* Ruled lines */}
              <RuledLines>
                {Array.from({ length: 16 }).map((_, i) => <RuledLine key={i} />)}
              </RuledLines>
              {/* DBMS note content */}
              <NoteContent>
                <NoteHeading>Chapter 4 — DBMS</NoteHeading>
                <NoteText $w="92%" $opacity={0.82}>Database Management Systems</NoteText>
                <NoteBlank />
                <NoteSubHeading>4.1 Relational Model</NoteSubHeading>
                <NoteText $w="95%" $opacity={0.72}>- Tables = relations, rows = tuples</NoteText>
                <NoteText $w="80%" $opacity={0.68}>- Primary key uniquely identifies a row</NoteText>
                <NoteText $w="88%" $opacity={0.70}>- Foreign key links two tables</NoteText>
                <NoteBlank />
                <NoteSubHeading>4.2 SQL Basics</NoteSubHeading>
                <NoteText $w="90%" $opacity={0.72} $italic>SELECT * FROM students WHERE gpa &gt; 3.5</NoteText>
                <NoteText $w="75%" $opacity={0.65}>- DDL: CREATE, ALTER, DROP</NoteText>
                <NoteText $w="82%" $opacity={0.68}>- DML: INSERT, UPDATE, DELETE</NoteText>
                <NoteBlank />
                <NoteSubHeading>4.3 Normalisation</NoteSubHeading>
                <NoteText $w="88%" $opacity={0.70}>1NF → 2NF → 3NF → BCNF</NoteText>
                <NoteText $w="70%" $opacity={0.60}>Eliminate redundancy &amp; anomalies</NoteText>
              </NoteContent>
            </BookBody>

            {/* Spine */}
            <BookSpine>
              <SpineText>NotesHub</SpineText>
            </BookSpine>

            {/* Cover flips open on scroll */}
            <BookCover $open={bookOpen}>
              <CoverFront>
                <CoverLogo>N</CoverLogo>
                <CoverTitle>NotesHub</CoverTitle>
                <CoverEdition>2025 Edition</CoverEdition>
              </CoverFront>
              <CoverBack />
            </BookCover>
          </BookWrap>
        </BookScene>

        <ScrollHint $hide={bookOpen}>
          <ScrollText>Scroll to open</ScrollText>
          <ScrollArrow />
        </ScrollHint>
      </HeroSection>

      {/* ── Revealed content ── */}
      <ContentWrapper $visible={contentVisible}>
        {/* Features */}
        <FeaturesSection>
          <SectionLabel>What you get</SectionLabel>
          <SectionTitle>Everything you need to study smarter</SectionTitle>
          <FeatureGrid>
            {FEATURES.map((f) => (
              <FeatureCard key={f.name}>
                <FeatureIcon>{f.icon}</FeatureIcon>
                <FeatureName>{f.name}</FeatureName>
                <FeatureDesc>{f.desc}</FeatureDesc>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </FeaturesSection>

        {/* CTA */}
        <CtaSection>
          <CtaTitle>Ready to open your notes?</CtaTitle>
          <CtaSub>
            Join thousands of students already sharing knowledge on NotesHub.
          </CtaSub>
          <ButtonRow>
            <PrimaryBtn to="/register">Get started — it's free</PrimaryBtn>
            <SecondaryBtn to="/login">Sign in</SecondaryBtn>
          </ButtonRow>
        </CtaSection>

        {/* Stats */}
        <StatsSection>
          <StatsInner>
            <div>
              <StatNum>10<span>k+</span></StatNum>
              <StatLabel>Notes shared</StatLabel>
            </div>
            <div>
              <StatNum>2<span>k+</span></StatNum>
              <StatLabel>Active students</StatLabel>
            </div>
            <div>
              <StatNum>50<span>+</span></StatNum>
              <StatLabel>Institutions</StatLabel>
            </div>
          </StatsInner>
        </StatsSection>
      </ContentWrapper>
    </Page>
  );
}
